import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { del, get, post } from '../utils/api';
import PageLayout from '../components/common/PageLayout';

function formatVariationOptionLabel(v) {
  if (!v) return '';
  const direct = String(v.display_name || v.name || '').trim();
  const sku = String(v.sku || '').trim();
  let attrs = v.attributes;
  if (typeof attrs === 'string') {
    try { attrs = JSON.parse(attrs); } catch { attrs = null; }
  }
  if (Array.isArray(attrs)) {
    const attrLabel = attrs.map((a) => String(a?.option || '').trim()).filter(Boolean).join(' · ');
    if (attrLabel && (!direct || direct === sku)) return attrLabel;
    if (attrLabel && direct && /^[-.\w]+$/.test(direct) && /[A-Za-z]/.test(attrLabel)) return attrLabel;
  }
  return direct || sku || `Variation #${v.id}`;
}

function sponsorKey(productId, variationId) {
  return `${Number(productId || 0)}::${variationId == null ? 'simple' : Number(variationId)}`;
}

function renderEventAssociationSummary(group, key) {
  if (!group) return null;
  const details = Array.isArray(group.event_variation_names) && group.event_variation_names.length > 0
    ? group.event_variation_names.join(', ')
    : 'No variations';

  return (
    <Box key={key} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.75, rowGap: 0.25 }}>
      <Typography component="span" variant="body2" sx={{ color: '#d97706' }}>
        {group.event_product_name}
      </Typography>
      <Typography component="span" variant="body2" color="text.primary">
        {details}
      </Typography>
    </Box>
  );
}

export default function ManageSponsorBenefitsPage() {
  const [benefitSets, setBenefitSets] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [associationNotice, setAssociationNotice] = React.useState('');

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [associationDialogOpen, setAssociationDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('create');
  const [managedContext, setManagedContext] = React.useState(null);
  const [editingAssociation, setEditingAssociation] = React.useState(null);
  const [expandedSponsors, setExpandedSponsors] = React.useState({});

  const [selectedSponsorProduct, setSelectedSponsorProduct] = React.useState(null);
  const [sponsorInput, setSponsorInput] = React.useState('');
  const [sponsorOptions, setSponsorOptions] = React.useState([]);
  const [variationOptions, setVariationOptions] = React.useState([]);
  const [selectedVariation, setSelectedVariation] = React.useState('');
  const [pendingVariationId, setPendingVariationId] = React.useState(null);

  const [eventInput, setEventInput] = React.useState('');
  const [eventOptions, setEventOptions] = React.useState([]);
  const [selectedEventProduct, setSelectedEventProduct] = React.useState(null);
  const [eventVariationOptions, setEventVariationOptions] = React.useState([]);
  const [selectedEventVariationIds, setSelectedEventVariationIds] = React.useState([]);

  const currentSponsorProductId = React.useMemo(() => {
    if (dialogMode === 'manage' && managedContext) return Number(managedContext.sponsor_product_id || 0);
    return Number(selectedSponsorProduct?.id || 0);
  }, [dialogMode, managedContext, selectedSponsorProduct]);

  const currentVariationId = React.useMemo(() => {
    if (dialogMode === 'manage' && managedContext) {
      return managedContext.sponsor_variation_id == null ? null : Number(managedContext.sponsor_variation_id);
    }
    if (variationOptions.length === 0) return null;
    const parsed = Number(selectedVariation || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [dialogMode, managedContext, variationOptions.length, selectedVariation]);

  const currentBenefitSet = React.useMemo(() => {
    const currentKey = sponsorKey(currentSponsorProductId, currentVariationId);
    if (dialogMode === 'manage' && managedContext?.benefit_set_id) {
      return benefitSets.find((row) => Number(row.benefit_set_id || 0) === Number(managedContext.benefit_set_id || 0))
        || benefitSets.find((row) => sponsorKey(row.sponsor_product_id, row.sponsor_variation_id) === currentKey)
        || managedContext;
    }
    if (!currentSponsorProductId) return null;
    return benefitSets.find((row) => sponsorKey(row.sponsor_product_id, row.sponsor_variation_id) === currentKey) || null;
  }, [benefitSets, currentSponsorProductId, currentVariationId, dialogMode, managedContext]);

  const currentEventGroups = Array.isArray(currentBenefitSet?.event_links) ? currentBenefitSet.event_links : [];
  const allSelectedEventVariations = eventVariationOptions.length > 0 && selectedEventVariationIds.length === eventVariationOptions.length;
  const someSelectedEventVariations = selectedEventVariationIds.length > 0 && !allSelectedEventVariations;

  const resetAssociationForm = React.useCallback(() => {
    setEditingAssociation(null);
    setSelectedEventProduct(null);
    setEventInput('');
    setEventVariationOptions([]);
    setSelectedEventVariationIds([]);
    setAssociationNotice('');
  }, []);

  const loadAllBenefits = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await get('/api/manage/sponsor-benefits');
      setBenefitSets(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setBenefitSets([]);
      setError(e?.message || 'Failed to load sponsor benefits');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { loadAllBenefits(); }, [loadAllBenefits]);

  React.useEffect(() => {
    if (!sponsorInput || sponsorInput.length < 2) {
      setSponsorOptions([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: sponsorInput, limit: 50 });
        if (!ignore) setSponsorOptions(Array.isArray(res?.products) ? res.products : []);
      } catch {
        if (!ignore) setSponsorOptions([]);
      }
    })();
    return () => { ignore = true; };
  }, [sponsorInput]);

  React.useEffect(() => {
    if (!eventInput || eventInput.length < 2) {
      setEventOptions([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: eventInput, limit: 50, for_event_report: '1' });
        if (!ignore) setEventOptions(Array.isArray(res?.products) ? res.products : []);
      } catch {
        if (!ignore) setEventOptions([]);
      }
    })();
    return () => { ignore = true; };
  }, [eventInput]);

  React.useEffect(() => {
    if (!selectedSponsorProduct?.id) {
      setVariationOptions([]);
      setSelectedVariation('');
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/variations', { product_id: String(selectedSponsorProduct.id), limit: 300 });
        const arr = Array.isArray(res?.variations) ? res.variations : [];
        if (!ignore) {
          setVariationOptions(arr);
          if (arr.length === 0) {
            setSelectedVariation('');
          } else if (pendingVariationId && arr.some((v) => String(v.id) === String(pendingVariationId))) {
            setSelectedVariation(String(pendingVariationId));
          } else {
            setSelectedVariation('');
          }
          setPendingVariationId(null);
        }
      } catch {
        if (!ignore) {
          setVariationOptions([]);
          setSelectedVariation('');
          setPendingVariationId(null);
        }
      }
    })();
    return () => { ignore = true; };
  }, [selectedSponsorProduct?.id, pendingVariationId]);

  React.useEffect(() => {
    if (!selectedEventProduct?.id) {
      setEventVariationOptions([]);
      setSelectedEventVariationIds([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/variations', { product_id: String(selectedEventProduct.id), limit: 300 });
        const arr = Array.isArray(res?.variations) ? res.variations : [];
        if (!ignore) {
          setEventVariationOptions(arr);
          setSelectedEventVariationIds((prev) => prev.filter((id) => arr.some((v) => Number(v.id) === Number(id))));
        }
      } catch {
        if (!ignore) {
          setEventVariationOptions([]);
          setSelectedEventVariationIds([]);
        }
      }
    })();
    return () => { ignore = true; };
  }, [selectedEventProduct?.id]);

  const openCreateDialog = () => {
    setDialogMode('create');
    setManagedContext(null);
    setSelectedSponsorProduct(null);
    setSponsorInput('');
    setVariationOptions([]);
    setSelectedVariation('');
    setPendingVariationId(null);
    resetAssociationForm();
    setDialogOpen(true);
  };

  const openManageDialog = (group) => {
    setDialogMode('manage');
    setManagedContext(group);
    setSelectedSponsorProduct({ id: group.sponsor_product_id, name: group.sponsor_product_name });
    setSponsorInput(group.sponsor_product_name || '');
    setPendingVariationId(group.sponsor_variation_id || null);
    setSelectedVariation(group.sponsor_variation_id != null ? String(group.sponsor_variation_id) : '');
    resetAssociationForm();
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    resetAssociationForm();
  };

  const closeAssociationDialog = () => {
    if (saving) return;
    setAssociationDialogOpen(false);
    resetAssociationForm();
  };

  const openAddAssociationDialog = () => {
    resetAssociationForm();
    setAssociationDialogOpen(true);
  };

  const openEditAssociationDialog = (group) => {
    setEditingAssociation(group);
    setSelectedEventProduct({ id: group.event_product_id, name: group.event_product_name });
    setEventInput(group.event_product_name || '');
    setSelectedEventVariationIds(Array.isArray(group.event_variation_ids) ? group.event_variation_ids.map((id) => Number(id)) : []);
    setAssociationDialogOpen(true);
  };

  const toggleEventVariation = (variationId, checked) => {
    const idNum = Number(variationId || 0);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    setSelectedEventVariationIds((prev) => {
      const set = new Set(prev.map((id) => Number(id)));
      if (checked) set.add(idNum);
      else set.delete(idNum);
      return Array.from(set.values()).sort((a, b) => a - b);
    });
  };

  const toggleAllEventVariations = (checked) => {
    if (!checked) {
      setSelectedEventVariationIds([]);
      return;
    }
    setSelectedEventVariationIds(eventVariationOptions.map((v) => Number(v.id)).filter((id) => Number.isFinite(id) && id > 0));
  };

  const onSaveAssociation = async () => {
    setError('');
    setOk('');
    setAssociationNotice('');

    if (!currentSponsorProductId) return setError('Select sponsor product first.');
    if (variationOptions.length > 0 && !currentVariationId) return setError('Select sponsor variation.');
    if (!selectedEventProduct?.id) return setError('Select event product.');
    if (eventVariationOptions.length > 0 && selectedEventVariationIds.length === 0) return setError('Select at least one event variation.');

    setSaving(true);
    try {
      const res = await post('/api/manage/sponsor-benefits', {
        sponsor_product_id: currentSponsorProductId,
        sponsor_variation_id: currentVariationId,
        event_product_id: Number(selectedEventProduct.id),
        event_variation_ids: selectedEventVariationIds,
      });
      await loadAllBenefits();
      const benefitSetId = Number(res?.benefit_set_id || 0);
      if (benefitSetId > 0) {
        const refreshed = benefitSets.find((row) => Number(row.benefit_set_id || 0) === benefitSetId);
        if (refreshed) setManagedContext(refreshed);
      }
      setOk(editingAssociation ? 'Benefit association updated.' : 'Benefit association added.');
      setAssociationNotice(
        editingAssociation
          ? 'Benefit association updated. Review the selections and click Close when you are finished.'
          : 'Benefit association added. You can keep working in this dialog or click Close when you are finished.'
      );
    } catch (e) {
      setError(e?.message || 'Failed to save sponsor benefit');
    } finally {
      setSaving(false);
    }
  };

  const onDeleteAssociationGroup = async (group) => {
    if (!currentBenefitSet?.benefit_set_id) return;
    if (!window.confirm(`Delete benefit mapping for "${group.event_product_name || group.event_product_id}"?`)) return;
    setError('');
    setOk('');
    try {
      const params = new URLSearchParams({
        benefit_set_id: String(currentBenefitSet.benefit_set_id),
        event_product_id: String(group.event_product_id),
      });
      await del(`/api/manage/sponsor-benefits?${params.toString()}`);
      await loadAllBenefits();
      setOk('Benefit mapping deleted.');
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  };

  const onDeleteSponsorGroup = async (group) => {
    const label = group.section_label || group.sponsor_product_name;
    if (!window.confirm(`Delete sponsor benefit "${label}" and all associated event links?`)) return;
    setError('');
    setOk('');
    try {
      await del(`/api/manage/sponsor-benefits/${group.benefit_set_id}`);
      await loadAllBenefits();
      if (dialogOpen && Number(currentBenefitSet?.benefit_set_id || 0) === Number(group.benefit_set_id || 0)) {
        setDialogOpen(false);
      }
      setOk('Sponsor benefit removed.');
    } catch (e) {
      setError(e?.message || 'Failed to delete sponsor benefit group');
    }
  };

  const toggleExpanded = (key) => {
    setExpandedSponsors((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const currentVariationLabel = managedContext?.sponsor_variation_name || 'No variations';

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Sponsor Benefits</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure sponsor variation benefits by linking sponsor products or variations to one or more event variations for Event Reports.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          Create Sponsor Benefit
        </Button>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {ok ? <Alert severity="success" sx={{ mb: 1.5 }}>{ok}</Alert> : null}

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #eee' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Configured Sponsor Benefits</Typography>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sponsor Product</TableCell>
              <TableCell>Sponsor Variation</TableCell>
              <TableCell>Benefitted Event Items</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && benefitSets.length === 0 ? (
              <TableRow><TableCell colSpan={4}>No sponsor benefits configured yet.</TableCell></TableRow>
            ) : benefitSets.map((group) => {
              const key = sponsorKey(group.sponsor_product_id, group.sponsor_variation_id);
              const expanded = !!expandedSponsors[key];
              const eventLinks = Array.isArray(group.event_links) ? group.event_links : [];
              const visible = expanded ? eventLinks : eventLinks.slice(0, 3);
              return (
                <TableRow key={key}>
                  <TableCell>{group.sponsor_product_name}</TableCell>
                  <TableCell>{group.sponsor_variation_name || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 700 }}>
                    {eventLinks.length === 0 ? (
                      <Typography variant="body2">-</Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {visible.map((eventGroup, idx) => renderEventAssociationSummary(eventGroup, `${key}-${idx}`))}
                        {eventLinks.length > 3 ? (
                          <Button size="small" variant="text" sx={{ alignSelf: 'flex-start', px: 0 }} onClick={() => toggleExpanded(key)}>
                            {expanded ? 'Show less' : `Show ${eventLinks.length - visible.length} more`}
                          </Button>
                        ) : null}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => openManageDialog(group)}>
                        Manage
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => onDeleteSponsorGroup(group)}>
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          closeDialog();
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Manage Sponsor Benefits</DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            {dialogMode === 'manage' ? (
              <>
                <TextField
                  sx={{ minWidth: 320 }}
                  label="Sponsor Product"
                  size="small"
                  value={managedContext?.sponsor_product_name || selectedSponsorProduct?.name || ''}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  sx={{ minWidth: 260 }}
                  label="Sponsor Variation"
                  size="small"
                  value={currentVariationLabel}
                  InputProps={{ readOnly: true }}
                />
              </>
            ) : (
              <>
                <Autocomplete
                  sx={{ minWidth: 320 }}
                  options={sponsorOptions}
                  value={selectedSponsorProduct}
                  onChange={(_, value) => {
                    setSelectedSponsorProduct(value || null);
                    setPendingVariationId(null);
                    setSelectedVariation('');
                  }}
                  inputValue={sponsorInput}
                  onInputChange={(_, value) => setSponsorInput(value)}
                  getOptionLabel={(option) => option?.name || ''}
                  renderInput={(params) => <TextField {...params} label="Sponsor Product" size="small" placeholder="Type sponsor product name" />}
                />
                <FormControl size="small" sx={{ minWidth: 260 }} disabled={!selectedSponsorProduct?.id || variationOptions.length === 0}>
                  <InputLabel>Sponsor Variation</InputLabel>
                  <Select
                    value={selectedVariation}
                    label="Sponsor Variation"
                    onChange={(e) => setSelectedVariation(String(e.target.value))}
                  >
                    {variationOptions.map((variation) => (
                      <MenuItem key={variation.id} value={String(variation.id)}>
                        {formatVariationOptionLabel(variation)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedSponsorProduct?.id && variationOptions.length === 0 ? (
                  <TextField sx={{ minWidth: 220 }} size="small" label="Sponsor Variation" value="No variations" InputProps={{ readOnly: true }} />
                ) : null}
              </>
            )}
            <Button variant="outlined" onClick={loadAllBenefits} disabled={loading}>
              Refresh
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddAssociationDialog}
              disabled={!currentSponsorProductId || (variationOptions.length > 0 && !currentVariationId)}
            >
              Add Association
            </Button>
          </Stack>

          {currentSponsorProductId ? (
            <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #eee' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Event Product</TableCell>
                    <TableCell>Event Variations</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && currentEventGroups.length === 0 ? (
                    <TableRow><TableCell colSpan={3}>No event benefits configured for this sponsor selection.</TableCell></TableRow>
                  ) : currentEventGroups.map((group) => (
                    <TableRow key={`${group.event_product_id}`}>
                      <TableCell>{group.event_product_name || `#${group.event_product_id}`}</TableCell>
                      <TableCell>
                        {group.event_variation_names.length > 0 ? group.event_variation_names.join(', ') : 'No variations'}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton size="small" onClick={() => openEditAssociationDialog(group)} aria-label="Edit association">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => onDeleteAssociationGroup(group)} aria-label="Delete association">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography color="text.secondary">Select sponsor product and variation to manage event benefits.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={associationDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          closeAssociationDialog();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editingAssociation ? 'Edit Benefit Association' : 'Add Benefit Association'}</DialogTitle>
        <DialogContent dividers>
          {associationNotice ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {associationNotice}
            </Alert>
          ) : null}
          <Autocomplete
            fullWidth
            options={eventOptions}
            value={selectedEventProduct}
            onChange={(_, value) => {
              setSelectedEventProduct(value || null);
              setSelectedEventVariationIds([]);
            }}
            inputValue={eventInput}
            onInputChange={(_, value) => setEventInput(value)}
            getOptionLabel={(option) => option?.name || ''}
            renderInput={(params) => <TextField {...params} label="Event Product" size="small" placeholder="Type event product name" />}
            sx={{ mb: 2 }}
          />

          {selectedEventProduct?.id ? (
            eventVariationOptions.length > 0 ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  p: 2,
                  bgcolor: '#faf8f2',
                  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Product Variation name</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Select one or more event variations to receive this sponsor benefit.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={<Checkbox checked={allSelectedEventVariations} indeterminate={someSelectedEventVariations} onChange={(e) => toggleAllEventVariations(e.target.checked)} />}
                    label="Select all"
                  />
                </Stack>
                <List
                  dense
                  disablePadding
                  sx={{
                    display: 'grid',
                    gap: 1,
                    maxHeight: 320,
                    overflowY: 'auto',
                    pr: 0.5,
                  }}
                >
                  {eventVariationOptions.map((variation) => {
                    const checked = selectedEventVariationIds.includes(Number(variation.id));
                    return (
                      <ListItem
                        key={variation.id}
                        disablePadding
                        sx={{
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: checked ? 'primary.main' : 'divider',
                          bgcolor: checked ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                          transition: 'background-color 120ms ease, border-color 120ms ease',
                        }}
                      >
                        <Checkbox checked={checked} onChange={(e) => toggleEventVariation(variation.id, e.target.checked)} sx={{ mr: 1 }} />
                        <ListItemText primary={formatVariationOptionLabel(variation)} />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ) : (
              <Alert severity="info">This event product has no variations. The benefit will apply at the product level.</Alert>
            )
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssociationDialog} disabled={saving}>Close</Button>
          <Button variant="contained" onClick={onSaveAssociation} disabled={saving || !currentSponsorProductId || !selectedEventProduct?.id}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}


